<template>
  <div class="mt-2 px-6 inactive-job-profiles-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div
      v-if="!libraryLoading"
    >
      <LibraryList>
        <LibraryListItemWrapper
          v-for="{
            ulid,
            title,
            hris_job_code,
            effective_date,
            job_level: jobLevel,
          } in jobProfiles"
          :key="ulid"
        >
          <router-link
            :to="{ query: { preview: ulid, type: 'jobProfileHris' } }"
          >
            <InactiveJobProfilesItem
              v-bind="{
                title,
                jobCode: hris_job_code,
                effectiveDate: effective_date,
                jobLevel,
              }"
            />
          </router-link>
        </LibraryListItemWrapper>
      </LibraryList>
    </div>

    <StickyPagination
      v-if="totalPages > 1"
      :model-value="page"
      :length="totalPages"
      @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router })"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppBase } from '@/stores/app/base';
import { useAppLoading } from '@/stores/app/loading';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { useFilters } from '@/stores/filters/filters';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import InactiveJobProfilesItem from '@/components/jobProfiles/InactiveJobProfilesItem.vue';
import { useAppPagination } from '@/stores/app/pagination';
import StickyPagination from '@/components/shared/StickyPagination.vue';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { jobProfiles } = storeToRefs(useJobProfiles());
const { loadJobProfiles } = useJobProfiles();
const {
  loadFilters, setFilterConfig, sendFilterRequest, setSearchTerm,
} = useFilters();
const { showFilterBarSettings } = useAppBase();
const { totalPages, page } = storeToRefs(useAppPagination());

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const loadPage = (params) => {
  const mode = params.mode === 'inactive-job-profiles' ? 'inactive' : params.mode;

  const updatedParams = {
    ...params,
    mode,
  };

  loadJobProfiles(updatedParams);
  loadFilters(updatedParams);
};

onMounted(() => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobProfiles',
    endpoint: 'jobProfileLibrary',
    showMyJobs: true,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  if (route.query?.q) setSearchTerm(route.query.q);

  loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    mode: 'inactive',
    searchTerm: route.query?.q || '',
    selectedMyJobs: route.query?.owned,
  });

  emit('track-page-load', { page: 'Inactive Job Profiles: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  setFilterConfig({
    subMenuType: null,
  });
});
</script>
